import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCache } from '../../contexts/CacheContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useSystemPreset } from '../../contexts/SystemPresetContext';
import { useTheme } from '../../contexts/ThemeContext';
import AnnouncementBar from '../../components/AnnouncementBar';
import Footer from '../../components/Footer';
import MorafaaLogo from '../../components/ui/MorafaaLogo';
import { isDemographicsComplete, isNewSignupFlow } from '../../utils/googleAuth';

const LoadingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoggedIn, setUser } = useAuth();
  const { 
    isLoaded: cacheLoaded, 
    isLoading: cacheLoading, 
    progress, 
    detailedLoading,
    loadCache,
    cache
  } = useCache();
  const { language, setLanguage, isRTL } = useLocalization();
  const { presetData, loading: presetLoading } = useSystemPreset();
  const { isDark, toggleTheme } = useTheme();
  
  const [isInitialized, setIsInitialized] = useState(false);
  const [migrationChecked, setMigrationChecked] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);

  console.log('🔄 LoadingPage rendered with user:', user);
  console.log('🔄 User ID:', user?.id);
  console.log('🔄 Is logged in:', isLoggedIn);
  console.log('🔄 Cache loaded:', cacheLoaded);
  console.log('🔄 Cache loading:', cacheLoading);
  console.log('🔄 Progress:', progress);
  console.log('🔄 Detailed loading:', detailedLoading);
  console.log('🔄 Preset data loading:', presetLoading);
  console.log('🔄 Preset data loaded:', !!presetData);
  
  // Enhanced debug logs for caching data
  console.log('📊 CACHE DATA DEBUG - Current cache state:');
  console.log('📊 - Offices count:', cache.offices?.length || 0);
  console.log('📊 - Clients count:', cache.clients?.length || 0);
  console.log('📊 - Events count:', cache.events?.length || 0);
  console.log('📊 - Legal Files count:', cache.legalFiles?.length || 0);
  console.log('📊 - Roles count:', cache.roles?.length || 0);
  console.log('📊 - System Preset Data:', cache.systemPresetData ? 'Loaded' : 'Not loaded');
  console.log('📊 - System Preset Data (from context):', presetData ? 'Loaded' : 'Not loaded');
  console.log('📊 - System Preset Data loading:', presetLoading);
  
  // Show detailed data when available
  if (cache.offices?.length > 0) {
    console.log('📊 OFFICES DATA:', cache.offices.map(o => ({
      id: o.id,
      name: o.name,
      address: o.address,
      phoneNumber: o.phoneNumber,
      membersCount: o.stuffMembersList?.length || 0
    })));
  }
  
  if (cache.clients?.length > 0) {
    console.log('📊 CLIENTS DATA:', cache.clients.map(c => ({
      id: c.id,
      name: c.name,
      mobileNumber: c.mobileNumber,
      email: c.email,
      rolesCount: c.rolesIDs?.length || 0,
      isArchived: c.isArchived,
      isDeleted: c.isDeleted
    })));
  }
  
  if (cache.events?.length > 0) {
    console.log('📊 EVENTS DATA:', cache.events.map(e => ({
      id: e.id,
      title: e.legalFileTitle,
      date: e.date,
      status: e.status,
      clientName: e.clientName,
      officeName: e.officeName
    })));
  }
  
  if (cache.legalFiles?.length > 0) {
    console.log('📊 LEGAL FILES DATA:', cache.legalFiles.map(f => ({
      id: f.id,
      title: f.title,
      clientID: f.clientID,
      statusID: f.statusID,
      lawyerID: f.lawyerID
    })));
  }
  
  if (cache.roles?.length > 0) {
    console.log('📊 ROLES DATA:', cache.roles.map(r => ({
      id: r.id,
      lawyerName: r.lawyerName,
      officeName: r.officeName,
      officeID: r.officeID,
      isOfficeCreator: r.isOfficeCreator,
      isOfficeAdmin: r.isOfficeAdmin,
      isArchived: r.isArchived,
      isDeleted: r.isDeleted
    })));
  }
  
  // System Preset Data debug logs
  if (presetData) {
    console.log('📊 SYSTEM PRESET DATA DEBUG - All 16 Types:');
    console.log('📊 - Attachment Types count:', presetData.attachmentTypes?.length || 0);
    console.log('📊 - Case Statuses count:', presetData.caseStatuses?.length || 0);
    console.log('📊 - Case Types count:', presetData.caseTypes?.length || 0);
    console.log('📊 - Contact Us Types count:', presetData.contactUsTypes?.length || 0);
    console.log('📊 - Court Types count:', presetData.courtTypes?.length || 0);
    console.log('📊 - Courts count:', presetData.courts?.length || 0);
    console.log('📊 - Event Types count:', presetData.eventTypes?.length || 0);
    console.log('📊 - File Statuses count:', presetData.fileStatuses?.length || 0);
    console.log('📊 - File Types count:', presetData.fileTypes?.length || 0);
    console.log('📊 - Financial Transaction Statuses count:', presetData.financialTransactionStatuses?.length || 0);
    console.log('📊 - Forum Categories count:', presetData.forumCategories?.length || 0);
    console.log('📊 - Lawyer Titles count:', presetData.lawyerTitles?.length || 0);
    console.log('📊 - Office Types count:', presetData.officeTypes?.length || 0);
    console.log('📊 - Payment Types count:', presetData.paymentTypes?.length || 0);
    console.log('📊 - Place Types count:', presetData.placeTypes?.length || 0);
    console.log('📊 - Specialities count:', presetData.specialities?.length || 0);
    console.log('📊 - Sub Specialities count:', presetData.subSpecialities?.length || 0);
    
    if (presetData.lawyerTitles?.length > 0) {
      console.log('📊 LAWYER TITLES:', presetData.lawyerTitles.map(t => ({
        id: t.id,
        name_en: t.name_en,
        name_ar: t.name_ar
      })));
    }
    
    if (presetData.eventTypes?.length > 0) {
      console.log('📊 EVENT TYPES:', presetData.eventTypes.map(t => ({
        id: t.id,
        name_en: t.name_en,
        name_ar: t.name_ar
      })));
    }
    
    if (presetData.placeTypes?.length > 0) {
      console.log('📊 PLACE TYPES:', presetData.placeTypes.map(t => ({
        id: t.id,
        name_en: t.name_en,
        name_ar: t.name_ar
      })));
    }
    
    if (presetData.courts?.length > 0) {
      console.log('📊 COURTS:', presetData.courts.map(c => ({
        id: c.id,
        name_en: c.name_en,
        name_ar: c.name_ar
      })));
    }
    
    if (presetData.contactUsTypes?.length > 0) {
      console.log('📊 CONTACT US TYPES:', presetData.contactUsTypes.map(t => ({
        id: t.id,
        name_en: t.name_en,
        name_ar: t.name_ar
      })));
    }
    
    if (presetData.forumCategories?.length > 0) {
      console.log('📊 FORUM CATEGORIES:', presetData.forumCategories.map(c => ({
        id: c.id,
        name_en: c.name_en,
        name_ar: c.name_ar,
        icon: c.icon,
        color: c.color,
        order: c.order,
        isActive: c.isActive
      })));
    }
  }

  // Initialize caching when component mounts
  useEffect(() => {
    if (isLoggedIn && user && !isInitialized) {
      console.log('🚀 LoadingPage: Starting data loading process...');
      setIsInitialized(true);
      loadCache();
    }
  }, [isLoggedIn, user, isInitialized, loadCache]);


  // Navigate to dashboard when all data is loaded
  useEffect(() => {
    if (
      isLoggedIn &&
      cacheLoaded &&
      !cacheLoading &&
      migrationChecked &&
      !isMigrating
    ) {
      console.log('🎉 LoadingPage: All data loaded, navigating to dashboard');
      console.log('🎉 LoadingPage: Cache data summary:', {
        rolesCount: detailedLoading.roles ? 'loaded' : 'not loaded',
        officesCount: detailedLoading.offices ? 'loaded' : 'not loaded',
        clientsCount: detailedLoading.clients ? 'loaded' : 'not loaded',
        eventsCount: detailedLoading.events ? 'loaded' : 'not loaded',
        legalFilesCount: detailedLoading.legalFiles ? 'loaded' : 'not loaded',
        systemDataCount: detailedLoading.systemPresetData ? 'loaded' : 'not loaded'
      });
      
      // Final comprehensive debug summary
      console.log('🎯 FINAL CACHE SUMMARY - All data loaded successfully:');
      console.log('🎯 - Total Offices:', cache.offices?.length || 0);
      console.log('🎯 - Total Clients:', cache.clients?.length || 0);
      console.log('🎯 - Total Events:', cache.events?.length || 0);
      console.log('🎯 - Total Legal Files:', cache.legalFiles?.length || 0);
      console.log('🎯 - Total Roles:', cache.roles?.length || 0);
      console.log('🎯 - System Preset Data:', presetData ? 'Available' : 'Not available');
      
      if (presetData) {
        console.log('🎯 SYSTEM PRESET SUMMARY - All 16 Types:');
        console.log('🎯 - Attachment Types:', presetData.attachmentTypes?.length || 0);
        console.log('🎯 - Case Statuses:', presetData.caseStatuses?.length || 0);
        console.log('🎯 - Case Types:', presetData.caseTypes?.length || 0);
        console.log('🎯 - Contact Us Types:', presetData.contactUsTypes?.length || 0);
        console.log('🎯 - Court Types:', presetData.courtTypes?.length || 0);
        console.log('🎯 - Courts:', presetData.courts?.length || 0);
        console.log('🎯 - Event Types:', presetData.eventTypes?.length || 0);
        console.log('🎯 - File Statuses:', presetData.fileStatuses?.length || 0);
        console.log('🎯 - File Types:', presetData.fileTypes?.length || 0);
        console.log('🎯 - Financial Transaction Statuses:', presetData.financialTransactionStatuses?.length || 0);
        console.log('🎯 - Forum Categories:', presetData.forumCategories?.length || 0);
        console.log('🎯 - Lawyer Titles:', presetData.lawyerTitles?.length || 0);
        console.log('🎯 - Office Types:', presetData.officeTypes?.length || 0);
        console.log('🎯 - Payment Types:', presetData.paymentTypes?.length || 0);
        console.log('🎯 - Place Types:', presetData.placeTypes?.length || 0);
        console.log('🎯 - Specialities:', presetData.specialities?.length || 0);
        console.log('🎯 - Sub Specialities:', presetData.subSpecialities?.length || 0);
      }
      
      // Small delay to show completion state
      setTimeout(() => {
        if (user && !isDemographicsComplete(user)) {
          navigate('/auth/demographics', { replace: true });
          return;
        }
        if (isNewSignupFlow()) {
          navigate('/auth/profile-photo', { replace: true });
          return;
        }
        navigate('/dashboard', { replace: true });
      }, 500);
    }
  }, [
    isLoggedIn, 
    cacheLoaded, 
    cacheLoading, 
    navigate, 
    detailedLoading, 
    cache, 
    presetData, 
    migrationChecked,
    isMigrating
  ]);



  // Handle new users with no roles - skip roles loading and go to MyOffices page
  useEffect(() => {
    if (isLoggedIn && presetData && (cache.roles?.length || 0) === 0) {
      console.log('🆕 LoadingPage: New user detected (no roles), navigating to MyOffices page');
      console.log('🆕 LoadingPage: System preset data loaded, but no roles found');
      
      // Small delay to show completion state
      setTimeout(() => {
        navigate('/dashboard/my-offices', { replace: true });
      }, 500);
    }
  }, [isLoggedIn, presetData, cache.roles?.length, navigate]);

  // Handle user migration - create trial subscription for existing users without subscription
  useEffect(() => {
    const handleUserMigration = async () => {
      console.log('🔄 LoadingPage: Migration check conditions:', {
        isLoggedIn,
        userId: user?.id,
        migrationChecked,
        cacheLoaded,
        cacheLoading
      });
      
      // Simplified condition - just check if user is logged in and migration hasn't been checked
      if (isLoggedIn && user?.id && !migrationChecked) {
        console.log('🔄 LoadingPage: Basic conditions met, checking for user migration...');
        
        // Set migrationChecked to true since migration service is no longer needed
        setMigrationChecked(true);
      } else {
        console.log('⏳ LoadingPage: Migration conditions not met yet, waiting...');
      }
    };

    handleUserMigration();
  }, [isLoggedIn, user?.id, migrationChecked]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoggedIn) {
      console.log('🚀 LoadingPage: User not logged in, redirecting to login');
      navigate('/login', { replace: true });
    }
  }, [isLoggedIn, navigate]);

  const getLoadingStep = () => {
    if (detailedLoading.userProfile) return isRTL ? 'جاري تحميل الملف الشخصي...' : 'Loading user profile...';
    if (detailedLoading.roles) return isRTL ? 'جاري تحميل الأدوار...' : 'Loading roles...';
    if (detailedLoading.offices) return isRTL ? 'جاري تحميل المكاتب...' : 'Loading offices...';
    if (detailedLoading.clients) return isRTL ? 'جاري تحميل العملاء...' : 'Loading clients...';
    if (detailedLoading.events) return isRTL ? 'جاري تحميل الأحداث...' : 'Loading events...';
    if (detailedLoading.legalFiles) return isRTL ? 'جاري تحميل الملفات القانونية...' : 'Loading legal files...';
    if (detailedLoading.systemPresetData) return isRTL ? 'جاري تحميل البيانات النظامية...' : 'Loading system data...';
    return isRTL ? 'جاري إعداد لوحة التحكم...' : 'Preparing your dashboard...';
  };

  const getLoadingIcon = (isCompleted: boolean, isCurrent: boolean) => {
    if (isCompleted) return '✅';
    if (isCurrent) return '🔄';
    return '⏳';
  };

  const getStepStatus = (stepName: string) => {
    switch (stepName) {
      case 'userProfile':
        return { completed: detailedLoading.userProfile, current: detailedLoading.userProfile };
      case 'roles':
        return { completed: detailedLoading.roles, current: detailedLoading.roles && !detailedLoading.offices };
      case 'offices':
        return { completed: detailedLoading.offices, current: detailedLoading.offices && !detailedLoading.clients };
      case 'clients':
        return { completed: detailedLoading.clients, current: detailedLoading.clients && !detailedLoading.events };
      case 'events':
        return { completed: detailedLoading.events, current: detailedLoading.events && !detailedLoading.legalFiles };
      case 'legalFiles':
        return { completed: detailedLoading.legalFiles, current: detailedLoading.legalFiles && !detailedLoading.systemPresetData };
      case 'systemPresetData':
        return { completed: detailedLoading.systemPresetData, current: detailedLoading.systemPresetData };
      default:
    return { completed: false, current: false };
  }
};

  return (
    <div className={`min-h-screen bg-background-light dark:bg-background-dark ${isRTL ? 'rtl' : 'ltr'}`}>
      <AnnouncementBar />
      
      {/* Header */}
      <div className="bg-primary dark:bg-primary-dark shadow-sm border-b border-primary/20 dark:border-primary-light/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <MorafaaLogo size="sm" variant="light" />
              <span className="text-xl font-bold text-white">
                {isRTL ? 'مرافعة' : 'Morafaa'}
              </span>
            </div>
            
            {/* Theme & Language Toggle */}
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-colors"
                title={language === 'ar' ? 'تبديل المظهر' : 'Toggle Theme'}
              >
                {isDark ? '☀️' : '🌙'}
              </button>
              
              <button
                onClick={() => setLanguage(language === 'ar' ? 'en' : 'ar')}
                className="px-3 py-1 text-sm rounded-md transition-colors bg-white/20 text-white hover:bg-white/30"
              >
                {language === 'ar' ? 'English' : 'العربية'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="min-h-screen bg-primary/5 flex items-center justify-center">
        <div className={`bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4 ${isRTL ? 'text-right' : 'text-left'}`}>
        <div className="text-center mb-6">
          <div className="spinner h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {isRTL ? 'جاري إعداد مساحة العمل الخاصة بك' : 'Setting up your workspace'}
          </h2>
          <p className="text-gray-600">{getLoadingStep()}</p>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {isRTL ? 'الملف الشخصي' : 'User Profile'}
            </span>
            <span className="text-lg">
              {getLoadingIcon(getStepStatus('userProfile').completed, getStepStatus('userProfile').current)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {isRTL ? 'الأدوار والصلاحيات' : 'Roles & Permissions'}
            </span>
            <span className="text-lg">
              {getLoadingIcon(getStepStatus('roles').completed, getStepStatus('roles').current)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {isRTL ? 'المكاتب' : 'Offices'}
            </span>
            <span className="text-lg">
              {getLoadingIcon(getStepStatus('offices').completed, getStepStatus('offices').current)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {isRTL ? 'العملاء' : 'Clients'}
            </span>
            <span className="text-lg">
              {getLoadingIcon(getStepStatus('clients').completed, getStepStatus('clients').current)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {isRTL ? 'الأحداث' : 'Events'}
            </span>
            <span className="text-lg">
              {getLoadingIcon(getStepStatus('events').completed, getStepStatus('events').current)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {isRTL ? 'الملفات القانونية' : 'Legal Files'}
            </span>
            <span className="text-lg">
              {getLoadingIcon(getStepStatus('legalFiles').completed, getStepStatus('legalFiles').current)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {isRTL ? 'البيانات النظامية' : 'System Data'}
            </span>
            <span className="text-lg">
              {getLoadingIcon(getStepStatus('systemPresetData').completed, getStepStatus('systemPresetData').current)}
            </span>
          </div>
        </div>

        {progress.percentage > 0 && (
          <div className="mt-6">
            <div className={`flex justify-between text-sm text-gray-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span>{isRTL ? 'التقدم' : 'Progress'}</span>
              <span>{Math.round(progress.percentage)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress.percentage}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Completion message */}
        {cacheLoaded && !cacheLoading && migrationChecked && (
          <div className="mt-4 text-center">
            <div className="text-green-600 text-sm font-medium">
              {isRTL ? 'تم تحميل جميع البيانات بنجاح!' : 'All data loaded successfully!'}
            </div>
            <div className="text-gray-500 text-xs mt-1">
              {isRTL ? 'جاري التوجه إلى لوحة التحكم...' : 'Redirecting to dashboard...'}
            </div>
          </div>
        )}

        </div>
      </div>
      
      <Footer />

    </div>
  );
};

export default LoadingPage;
